import React, { useState, useEffect, useRef, useMemo } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import { Row, Col, Nav } from "react-bootstrap";
import { loadingCard } from "../../../helpers/loading";
import { fetchJobs, fetchReportJobs } from "../../../services/httpClient";

ChartJS.register(ArcElement, Tooltip, Legend);

interface DepartmentItem {
  id: number;
  name: string;
  key: string;
  count: number;
}

interface JobItem {
  id: number;
  name: string;
  department_id: number;
  department: string;
  positions_filled: number;
  positions_open: number;
  positions_total: number;
}

interface JobStackedBarChartProps {
  departments?: DepartmentItem[];
  jobs?: JobItem[];
  showHeader?: boolean;
}

export const JobsStackedBarChart = ({ departments, jobs, showHeader = false }: JobStackedBarChartProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchedDepartments, setFetchedDepartments] = useState<DepartmentItem[]>(); // | null>(null);
  const [departmentGroups, setDepartmentGroups] = useState<Record<number, JobItem[]>>({});
  const [selectedDepartmentKey, setSelectedDepartmentKey] = useState<any>(String(Object.keys(departmentGroups)[0]));
  const chartRef = useRef<any>(null);

  // Fetch data if not provided
  useEffect(() => {
    if (!departments || !jobs) {
      getJobData();
    } else {
      setFetchedDepartments(departments);
      const deptGroups = groupByDepartment(departments, jobs);
      setDepartmentGroups(deptGroups);
      setIsLoading(false);
    }
  }, [departments, jobs]);

  const getJobData = () => {
    fetchReportJobs()
      .then((res: any) => {
        const reportJobs = res[0].data;
        const deptCounts = reportJobs.reduce((acc: Record<string, number>, job: any) => {
          const dept = job.department;
          acc[dept] = (acc[dept] || 0) + 1;
          return acc;
        }, {});

        fetchJobs()
          .then((res: any) => {
            const jobs = res[0].data;

            const deptMap = new Map<string, any>();
            jobs.map((j: any) => {
              const deptID = j.department_id;
              const deptName = j.department;

              const count = deptCounts[deptName] ?? 0;
              if (!deptMap.has(deptName)) {
                deptMap.set(deptName, { id: deptID, name: `${deptName} (${count})`, key: deptName, count });
              }

              const open = j.positions_total - j.positions_filled;
              return {
                ...j,
                positions_open: open,
              };
            });

            Object.entries(deptCounts).forEach(([dept, count]) => {
              const key = dept;
              if (!deptMap.has(key)) {
                deptMap.set(key, { name: `${key} (${count})`, key, count });
              } else {
                const existing = deptMap.get(key);
                existing.count = count;
                existing.name = `${key} (${count})`;
              }
            });

            const departments = Array.from(deptMap.values()).sort((a: any, b: any) => a.key.localeCompare(b.key));
            setFetchedDepartments(departments);

            const deptGroups = groupByDepartment(departments, jobs);
            setDepartmentGroups(deptGroups);
          })
          .catch((err: any) => {
            console.log(err);
          });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  function groupByDepartment(depts: DepartmentItem[], jobs: JobItem[]) {
    let deptList: Record<string, JobItem[]> = {};

    depts.map((dept: DepartmentItem) => {
      const filteredJobs = jobs.filter((job: JobItem) => job.department_id === dept.id);
      deptList[dept.key] = filteredJobs;
    });

    if (deptList) {
      setSelectedDepartmentKey(String(Object.keys(deptList)[0]));
    }
    return deptList;
  }

  const chartData = useMemo(() => {
    const jobs = departmentGroups[selectedDepartmentKey] ?? [];

    return {
      labels: jobs.map((j) => j.name),
      datasets: [
        {
          label: "Filled",
          data: jobs.map((j) => j.positions_filled),
          backgroundColor: "#7d9158",
        },
        {
          label: "Open",
          data: jobs.map((j) => j.positions_open),
          backgroundColor: "#da7e39",
        },
      ],
    };
  }, [selectedDepartmentKey, departmentGroups]);

  const options = {
    indexAxis: "y" as const,
    maintainAspectRation: true,
    responsive: true,
    datasets: {
      bar: {
        maxBarThickness: 30,
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: `${selectedDepartmentKey} Department`,
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 4,
      },
    },
  };

  if (isLoading) {
    return loadingCard();
  }

  return (
    <>
      {showHeader && <h5 className="app-header-text">Job Assignments</h5>}
      <Row className="justify-content-start mb-5">
        <Col xs={4} lg={3}>
          <Nav variant="pills" className="flex-column">
            {fetchedDepartments?.map((dept) => (
              <Nav.Item className="d-grid" key={dept.id}>
                <Nav.Link
                  eventKey={dept.id}
                  className={selectedDepartmentKey === dept.key ? "nav-vertical-active" : "nav-vertical"}
                  onClick={() => {
                    setSelectedDepartmentKey(dept.key);
                  }}
                  style={{ textAlign: "left", fontWeight: 500, cursor: "pointer" }}
                >
                  {dept.key}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
        <Col xs={8} lg={9}>
          <div style={{ height: `${chartData.labels.length * 50}px`, minHeight: "350px" }}>
            {chartData.labels.length > 0 && <Bar ref={chartRef} data={chartData} options={options} />}
          </div>
        </Col>
      </Row>
    </>
  );
};
